// =====================================================================
// PLENARIUM:ESTRUTURA_SESSAO -- como a sessao desta camara e organizada
//  - expediente: 'DIVIDIDO' (Pequeno + Grande Expediente) ou 'UNICO'
//  - ata: 'EXPEDIENTE' (dentro do Expediente) ou 'ANTES' (parte propria,
//    depois da abertura e antes do Expediente)
//  - chamadas: 'UMA' ou 'DUAS' (2a chamada antes da Ordem do Dia -- PLENARIUM:DUAS_CHAMADAS)
// Vale para a camara toda: roteiro, fases da tribuna e Ata com IA.
// =====================================================================
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Settings, X, Check } from 'lucide-react';

export const ESTRUTURA_PADRAO = { expediente: 'DIVIDIDO', ata: 'EXPEDIENTE', chamadas: 'UMA', quorumAbertura: null, quorumVotacao: null };
const nq = (v) => { const n = parseInt(v, 10); return n >= 1 && n <= 200 ? n : null; }; // PLENARIUM:QUORUM_CAMARA
const normalizar = (x) => ({
  expediente: x && x.expediente === 'UNICO' ? 'UNICO' : 'DIVIDIDO',
  ata: x && x.ata === 'ANTES' ? 'ANTES' : 'EXPEDIENTE',
  chamadas: x && x.chamadas === 'DUAS' ? 'DUAS' : 'UMA',
  quorumAbertura: x ? nq(x.quorumAbertura) : null,
  quorumVotacao: x ? nq(x.quorumVotacao) : null
});
// PLENARIUM:QUORUM_CAMARA -- minimo de presentes: numero da camara ou, sem ele, a regra que a tela ja usava
export const quorumDaCamara = (e, fase, padrao) => (e && (fase === 'votacao' ? e.quorumVotacao : e.quorumAbertura)) || padrao;

let cache = null;
let promessa = null;
const ouvintes = new Set();
const avisar = (v) => ouvintes.forEach(f => { try { f(v); } catch (e) { /* segue */ } });

export function carregarEstruturaSessao(forcar = false) {
  if (cache && !forcar) return Promise.resolve(cache);
  if (promessa && !forcar) return promessa;
  promessa = axios.get('/api/estrutura-sessao')
    .then(r => { cache = normalizar(r.data); avisar(cache); return cache; })
    .catch(() => cache || { ...ESTRUTURA_PADRAO })
    .finally(() => { promessa = null; });
  return promessa;
}

export function useEstruturaSessao() {
  const [e, setE] = useState(cache || ESTRUTURA_PADRAO);
  useEffect(() => {
    let vivo = true;
    const f = (v) => { if (vivo) setE(v); };
    ouvintes.add(f);
    carregarEstruturaSessao().then(f);
    return () => { vivo = false; ouvintes.delete(f); };
  }, []);
  return e;
}

// Fases oferecidas ao abrir inscricoes da tribuna
export function fasesTribuna(e) {
  return e && e.expediente === 'UNICO'
    ? ['Expediente', 'Explicações Pessoais', 'Tribuna Livre', 'Comunicações']
    : ['Pequeno Expediente', 'Grande Expediente', 'Explicações Pessoais', 'Tribuna Livre', 'Comunicações'];
}

const CSS = `
.pes-op { display: flex; gap: 10px; align-items: flex-start; padding: 10px 12px; border: 1.5px solid var(--cinza, #e2e8f0); border-radius: 10px; cursor: pointer; background: #fff; text-align: left; width: 100%; font: inherit; color: inherit; }
.pes-op + .pes-op { margin-top: 8px; }
.pes-op.on { border-color: var(--azul, #2563eb); background: #eff6ff; }
.pes-op .pes-bola { width: 18px; height: 18px; border-radius: 50%; border: 2px solid #94a3b8; flex-shrink: 0; margin-top: 2px; display: flex; align-items: center; justify-content: center; }
.pes-op.on .pes-bola { border-color: var(--azul, #2563eb); background: var(--azul, #2563eb); color: #fff; }
.pes-op b { display: block; font-size: 0.85rem; }
.pes-op span.pes-d { display: block; font-size: 0.74rem; color: var(--texto-suave, #64748b); margin-top: 2px; }
.pes-grupo { font-size: 0.72rem; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; color: var(--texto-suave, #64748b); margin: 14px 0 8px; }
.pes-fluxo { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; font-size: 0.74rem; margin-top: 14px; padding: 10px 12px; background: #f8fafc; border-radius: 10px; }
.pes-fluxo i { font-style: normal; color: #94a3b8; }
.pes-fluxo span { background: #fff; border: 1px solid #e2e8f0; border-radius: 999px; padding: 2px 9px; font-weight: 600; }
.pes-fluxo span.ata { border-color: #93c5fd; color: #1d4ed8; }
`;

function Opcao({ on, titulo, desc, onClick }) {
  return (
    <button type="button" className={'pes-op' + (on ? ' on' : '')} onClick={onClick}>
      <span className="pes-bola">{on && <Check size={12} />}</span>
      <span><b>{titulo}</b><span className="pes-d">{desc}</span></span>
    </button>
  );
}

function fluxo(v) {
  const p = [v.chamadas === 'DUAS' ? 'Abertura e 1ª chamada' : 'Abertura e chamada'];
  const ataTxt = 'Ata da sessão anterior';
  if (v.ata === 'ANTES') p.push(ataTxt);
  const exp = v.expediente === 'UNICO' ? ['Expediente'] : ['Pequeno Expediente', 'Grande Expediente'];
  exp.forEach((x, i) => p.push(i === 0 && v.ata === 'EXPEDIENTE' ? `${x} (com a ata)` : x));
  if (v.chamadas === 'DUAS') p.push('2ª chamada');
  p.push('Ordem do Dia', 'Encerramento');
  return p;
}

export function BotaoEstruturaSessao({ className = 'btn btn-secondary' }) {
  const [aberto, setAberto] = useState(false);
  const [v, setV] = useState(ESTRUTURA_PADRAO);
  const [salvando, setSalvando] = useState(false);
  const [nVer, setNVer] = useState(0); // PLENARIUM:QUORUM_CAMARA
  const abrir = async () => { setV(await carregarEstruturaSessao(true)); setAberto(true); axios.get('/api/vereadores').then(r => setNVer((r.data || []).filter(x => x.ativo !== false).length)).catch(() => {}); };
  const salvar = async () => {
    setSalvando(true);
    try {
      const r = await axios.put('/api/estrutura-sessao', v);
      cache = normalizar(r.data); avisar(cache);
      toast.success('Estrutura da sessão salva.');
      setAberto(false);
    } catch (e) {
      toast.error((e.response && e.response.data && e.response.data.error) || 'Erro ao salvar.');
    } finally { setSalvando(false); }
  };
  return (
    <>
      <button type="button" className={className} onClick={abrir} title="Como as sessões desta câmara são organizadas"><Settings size={14} /> Estrutura</button>
      {aberto && (
        <div className="modal-overlay" onClick={() => setAberto(false)}>
          <style>{CSS}</style>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <h2 className="modal-title"><Settings size={16} /> Estrutura da sessão</h2>
              <button className="btn-modal-close" onClick={() => setAberto(false)}><X size={18} /></button>
            </div>
            <p style={{ fontSize: '0.78rem', color: 'var(--texto-suave)', marginBottom: 4 }}>
              Vale para <b>todas as sessões</b> desta câmara: roteiro do Presidente, fases da tribuna, chamadas e Ata com IA.
            </p>

            <div className="pes-grupo">Expediente</div>
            <Opcao on={v.expediente === 'DIVIDIDO'} titulo="Pequeno e Grande Expediente" desc="O Expediente é dividido em duas partes." onClick={() => setV({ ...v, expediente: 'DIVIDIDO' })} />
            <Opcao on={v.expediente === 'UNICO'} titulo="Expediente único" desc="Uma só parte, chamada apenas “Expediente”." onClick={() => setV({ ...v, expediente: 'UNICO' })} />

            <div className="pes-grupo">Ata da sessão anterior</div>
            <Opcao on={v.ata === 'EXPEDIENTE'} titulo="Dentro do Expediente" desc={`Lida e votada no início do ${v.expediente === 'UNICO' ? 'Expediente' : 'Pequeno Expediente'}.`} onClick={() => setV({ ...v, ata: 'EXPEDIENTE' })} />
            <Opcao on={v.ata === 'ANTES'} titulo="Fora do Expediente" desc="Parte própria, logo depois da abertura e da chamada, antes do Expediente." onClick={() => setV({ ...v, ata: 'ANTES' })} />

            <div className="pes-grupo">Chamada de presença</div>
            <Opcao on={v.chamadas !== 'DUAS'} titulo="Uma chamada" desc="Só a chamada da abertura." onClick={() => setV({ ...v, chamadas: 'UMA' })} />
            <Opcao on={v.chamadas === 'DUAS'} titulo="Duas chamadas" desc="A 2ª chamada é feita antes da Ordem do Dia. Quem não responder a ela tem falta na sessão." onClick={() => setV({ ...v, chamadas: 'DUAS' })} />

            {/* PLENARIUM:QUORUM_CAMARA */}
            <div className="pes-grupo">Quórum (mínimo de vereadores presentes)</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {[['quorumAbertura', 'Para abrir a sessão'], ['quorumVotacao', 'Para as votações']].map(([k, rot]) => (
                <label key={k} style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700 }}>{rot}
                  <input type="number" min="1" max={nVer || 200} className="form-input" style={{ marginTop: 4 }} value={v[k] || ''} placeholder={nVer ? `maioria absoluta (${Math.floor(nVer / 2) + 1})` : 'maioria absoluta'} onChange={e => setV({ ...v, [k]: e.target.value === '' ? null : parseInt(e.target.value, 10) || null })} />
                </label>
              ))}
            </div>
            <p style={{ fontSize: '0.74rem', color: 'var(--texto-suave)', margin: '6px 0 0' }}>Conforme o Regimento Interno ou a Lei Orgânica. Em branco, vale a maioria absoluta (metade + 1){nVer ? ` — ${Math.floor(nVer / 2) + 1} de ${nVer} vereadores` : ''}. Ex.: 1/4 de 9 vereadores = 3.</p>
            {nVer > 0 && ((v.quorumAbertura || 0) > nVer || (v.quorumVotacao || 0) > nVer) && <p style={{ fontSize: '0.78rem', color: '#b91c1c', fontWeight: 700, margin: '6px 0 0' }}>O quórum não pode passar de {nVer} (total de vereadores).</p>}

            <div className="pes-fluxo">
              {fluxo(v).map((x, i) => (
                <React.Fragment key={x}>
                  {i > 0 && <i>›</i>}
                  <span className={/ata|chamada$/i.test(x) && !/^Abertura/.test(x) ? 'ata' : ''}>{x}</span>
                </React.Fragment>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setAberto(false)}>Cancelar</button>
              <button type="button" className="btn btn-primary" disabled={salvando} onClick={salvar}>{salvando ? 'Salvando…' : 'Salvar'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
